/**
 * KadmiAI — Capacitor native bridge
 * Initialise les plugins natifs (push, status bar, keyboard, deep links)
 * Ce fichier est no-op sur le web, actif uniquement dans l'app native.
 */
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

/** Status Bar — masque l'overlay sur iOS */
async function initStatusBar() {
  if (!isNative) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#7FB342' });
    }
  } catch (e) {
    console.warn('[native] StatusBar init skipped:', e.message);
  }
}

/** Keyboard — gere le resize sur iOS */
async function initKeyboard() {
  if (!isNative) return;
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-open');
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-open');
    });
  } catch (e) {
    console.warn('[native] Keyboard init skipped:', e.message);
  }
}

/** Push Notifications — demande la permission et enregistre le token */
async function initPushNotifications() {
  if (!isNative) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('[native] Push permission denied');
      return;
    }

    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      console.log('[native] Push token:', token.value);
      // Envoyer le token au backend pour les notifications serveur
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const authToken = localStorage.getItem('kadmiai_token');
      if (authToken && API_URL) {
        fetch(`${API_URL}/api/push/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            token: token.value,
            platform: Capacitor.getPlatform()
          })
        }).catch(() => {});
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('[native] Push registration error:', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[native] Notification received:', notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[native] Notification action:', action);
      // Deep link navigation based on notification data
      const data = action.notification.data;
      if (data?.url) {
        window.location.href = data.url;
      }
    });
  } catch (e) {
    console.warn('[native] Push init skipped:', e.message);
  }
}

/** App — back button handler (Android) + deep links */
async function initAppListeners() {
  if (!isNative) return;
  try {
    const { App } = await import('@capacitor/app');

    // Android back button
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Deep links (Universal Links / App Links)
    App.addListener('appUrlOpen', (event) => {
      const url = new URL(event.url);
      const path = url.pathname;
      if (path) {
        window.location.href = path;
      }
    });

    // Resume from background
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        console.log('[native] App resumed');
      }
    });
  } catch (e) {
    console.warn('[native] App listeners skipped:', e.message);
  }
}

/** Splash Screen — masque apres le chargement */
async function hideSplash() {
  if (!isNative) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) {
    console.warn('[native] SplashScreen hide skipped:', e.message);
  }
}

/**
 * Initialise tous les plugins natifs.
 * Appeler depuis index.js apres le premier render.
 */
export async function initNative() {
  if (!isNative) return;
  console.log(`[native] Platform: ${Capacitor.getPlatform()}`);
  await Promise.all([
    initStatusBar(),
    initKeyboard(),
    initAppListeners()
  ]);
  // Push apres les autres pour eviter le blocage du dialogue permission
  await initPushNotifications();
  // Masquer le splash une fois tout charge
  await hideSplash();
}

export { isNative };
export default initNative;
